/**
 * SanPlumb — Datos normativos IS.010
 *
 * Fuente: Reglamento Nacional de Edificaciones (RNE) — Norma IS.010
 *         "Instalaciones Sanitarias para Edificaciones" — Perú.
 *
 * Origen de migración: legacy `sanplumb-mvp__1_.html` líneas 429-484 (objeto IS010)
 *                       + función `getDotacionDiaria()` línea 1958 (dotaciones AF
 *                         que estaban inline, ahora extraídas a tablas).
 *
 * Cobertura:
 *   §2.2  Dotaciones agua fría (multifamiliar + unifamiliar)
 *   §2.3  Velocidades máximas + diámetros
 *   §2.4  Almacenamiento (cisterna + tanque elevado)
 *   §3.2  Dotaciones agua caliente
 *   §3.4  Capacidad terma
 *   Anexo 1: aparatos uso privado (UG)
 *   Anexo 2: aparatos uso público (UG)
 *   Anexo 3: tabla Hunter (UG → caudal probable L/s)
 *
 * Unidades:
 *   Caudales: L/s
 *   Volúmenes: L (litros)
 *   Velocidades: m/s
 *   Diámetros: mm (nominales)
 */

import type { Diameter } from '../types';

// ──────────────────────────────────────────────────────────────────
// ANEXO 1 — Aparatos uso privado (residencial)
// ──────────────────────────────────────────────────────────────────

export interface AparatoUG {
  id: string;
  label: string;
  /** Unidades de Gasto agua fría. */
  ug_af: number;
  /** Unidades de Gasto agua caliente. */
  ug_ac: number;
  /** Unidades de Gasto totales (NO es la suma — es un valor combinado normado). */
  ug_total: number;
}

export const APARATOS_PRIVADO: readonly AparatoUG[] = [
  { id: 'inodoro_tanque', label: 'Inodoro c/tanque', ug_af: 3, ug_ac: 0, ug_total: 3 },
  { id: 'inodoro_red', label: 'Inodoro tanque desc. reducida', ug_af: 1.5, ug_ac: 0, ug_total: 1.5 },
  { id: 'inodoro_valv', label: 'Inodoro c/válvula semiaut.', ug_af: 6, ug_ac: 0, ug_total: 6 },
  { id: 'lavatorio', label: 'Lavatorio', ug_af: 0.75, ug_ac: 0.75, ug_total: 1 },
  { id: 'lavadero', label: 'Lavadero de ropa', ug_af: 2, ug_ac: 2, ug_total: 3 },
  { id: 'ducha', label: 'Ducha', ug_af: 1.5, ug_ac: 1.5, ug_total: 2 },
  { id: 'tina', label: 'Tina/bañera', ug_af: 1.5, ug_ac: 1.5, ug_total: 2 },
  { id: 'bidet', label: 'Bidet', ug_af: 0.75, ug_ac: 0.75, ug_total: 1 },
  { id: 'urinario', label: 'Urinario c/tanque', ug_af: 3, ug_ac: 0, ug_total: 3 },
] as const;

// ──────────────────────────────────────────────────────────────────
// ANEXO 2 — Aparatos uso público
// ──────────────────────────────────────────────────────────────────

export const APARATOS_PUBLICO: readonly AparatoUG[] = [
  { id: 'inodoro_tanque', label: 'Inodoro c/tanque', ug_af: 5, ug_ac: 0, ug_total: 5 },
  { id: 'inodoro_red', label: 'Inodoro tanque desc. reducida', ug_af: 2.5, ug_ac: 0, ug_total: 2.5 },
  { id: 'inodoro_valv', label: 'Inodoro válvula semiautomática', ug_af: 8, ug_ac: 0, ug_total: 8 },
  { id: 'lavatorio', label: 'Lavatorio corriente', ug_af: 1.5, ug_ac: 0.75, ug_total: 2 },
  { id: 'lavatorio_hotel', label: 'Lavatorio hotel/rest.', ug_af: 2, ug_ac: 1, ug_total: 3 },
  { id: 'lavadero', label: 'Lavadero de ropa', ug_af: 2, ug_ac: 2, ug_total: 3 },
  { id: 'ducha', label: 'Ducha', ug_af: 2, ug_ac: 2, ug_total: 3 },
  { id: 'tina', label: 'Tina/bañera', ug_af: 2, ug_ac: 2, ug_total: 3 },
  { id: 'bidet', label: 'Bidet', ug_af: 1, ug_ac: 0.75, ug_total: 1.5 },
  { id: 'urinario_tanque', label: 'Urinario c/tanque', ug_af: 3, ug_ac: 0, ug_total: 3 },
  { id: 'urinario_valv', label: 'Urinario válvula semiaut.', ug_af: 5, ug_ac: 0, ug_total: 5 },
  { id: 'bebedero', label: 'Bebedero', ug_af: 1, ug_ac: 0, ug_total: 1 },
] as const;

// ──────────────────────────────────────────────────────────────────
// ANEXO 3 — Tabla Hunter (UG → caudal probable en L/s)
// ──────────────────────────────────────────────────────────────────

/**
 * Fila de la tabla Hunter.
 * - `ug`: Unidades de Gasto acumuladas
 * - `q_tanque`: Caudal probable (L/s) con descarga por tanque
 * - `q_valvula`: Caudal probable (L/s) con válvula semiautomática.
 *               null para UG bajas donde la columna no aplica.
 */
export type HunterRow = readonly [ug: number, q_tanque: number, q_valvula: number | null];

export const HUNTER_TABLE: readonly HunterRow[] = [
  [3, 0.12, null], [4, 0.16, null], [5, 0.23, 0.91], [6, 0.25, 0.94], [7, 0.28, 0.97],
  [8, 0.29, 1.0], [9, 0.32, 1.03], [10, 0.43, 1.06], [12, 0.38, 1.12], [14, 0.42, 1.17],
  [16, 0.46, 1.22], [18, 0.5, 1.27], [20, 0.54, 1.33], [22, 0.58, 1.37], [24, 0.61, 1.42],
  [26, 0.67, 1.45], [28, 0.71, 1.51], [30, 0.75, 1.55], [32, 0.79, 1.59], [34, 0.82, 1.63],
  [36, 0.85, 1.67], [38, 0.88, 1.7], [40, 0.91, 1.74], [42, 0.95, 1.78], [44, 1.0, 1.82],
  [46, 1.03, 1.84], [50, 1.03, 1.84], [60, 1.13, 1.92], [70, 1.2, 2.0], [80, 1.28, 2.09],
  [90, 1.37, 2.18], [100, 1.61, 3.55], [120, 1.91, 2.8], [150, 2.14, 3.04], [200, 2.53, 3.44],
  [300, 3.15, 4.04], [400, 3.67, 4.58], [500, 3.97, 4.72], [1000, 7.04, 7.65],
] as const;

// NOTA: El valor q_valvula = 3.55 en ug=100 parece anómalo vs sus vecinos
//       (90→2.18, 120→2.80). Se preserva el valor del MVP — verificar contra
//       norma vigente antes de cerrar Fase 2 (hunter.ts).

// ──────────────────────────────────────────────────────────────────
// §2.3(f) — Velocidades máximas por diámetro nominal
// ──────────────────────────────────────────────────────────────────

/** Velocidad máxima permitida (m/s) por diámetro nominal (mm). */
export const VELOCIDADES_MAX: Readonly<Record<number, number>> = {
  15: 1.9,
  20: 2.2,
  25: 2.48,
  32: 2.85,
  40: 3.0,
};

/** Mapeo Diameter (string) → mm para usar con VELOCIDADES_MAX. */
export const DIAM_TO_MM: Readonly<Record<Diameter, number>> = {
  '1/2"': 15,
  '3/4"': 20,
  '1"': 25,
  '1¼"': 32,
};

// ──────────────────────────────────────────────────────────────────
// §2.2 — Dotaciones agua fría
// ──────────────────────────────────────────────────────────────────

/** Dotación AF para vivienda multifamiliar (L/departamento/día) por número de dormitorios. */
export const DOTACION_AF_MULTIFAMILIAR: Readonly<Record<number, number>> = {
  1: 500,
  2: 850,
  3: 1200,
  4: 1350,
  5: 1500,
};

/** Fila de la tabla de dotación AF unifamiliar por área del lote. */
export interface DotacionUnifamiliarRow {
  /** Área máxima del lote en m² para la cual aplica esta dotación. */
  areaMaxM2: number;
  /** Dotación diaria en litros. */
  dotacionL: number;
}

/** Dotación AF para vivienda unifamiliar (L/lote/día) por área del lote.
 *  Se aplica el primer match (areaMaxM2 >= área); el último valor (2500L) aplica
 *  para áreas > 700 m². */
export const DOTACION_AF_UNIFAMILIAR: readonly DotacionUnifamiliarRow[] = [
  { areaMaxM2: 200, dotacionL: 1500 },
  { areaMaxM2: 300, dotacionL: 1700 },
  { areaMaxM2: 400, dotacionL: 1900 },
  { areaMaxM2: 500, dotacionL: 2100 },
  { areaMaxM2: 600, dotacionL: 2200 },
  { areaMaxM2: 700, dotacionL: 2300 },
  { areaMaxM2: Infinity, dotacionL: 2500 },
] as const;

// ──────────────────────────────────────────────────────────────────
// §3.2 — Dotación agua caliente
// ──────────────────────────────────────────────────────────────────

/** Dotación AC por vivienda/día (L/día) por número de dormitorios (hasta 5). */
export const DOTACION_AC: Readonly<Record<number, number>> = {
  1: 120,
  2: 250,
  3: 390,
  4: 420,
  5: 450,
};

/** Para más de 5 dormitorios: D_AC = 450 + (n − 5) × 80. */
export const DOTACION_AC_EXTRA_POR_DORM = 80;

// ──────────────────────────────────────────────────────────────────
// §2.4 — Almacenamiento
// ──────────────────────────────────────────────────────────────────

export const ALMACENAMIENTO = {
  /** Cisterna: ≥ 3/4 de la dotación diaria total. */
  CISTERNA_FRAC: 0.75,
  /** Tanque elevado: ≥ 1/3 de la dotación diaria total. */
  TANQUE_ELEVADO_FRAC: 1 / 3,
  /** Mínimo absoluto para cualquiera de los dos. */
  MIN_LITROS: 1000,
} as const;

// ──────────────────────────────────────────────────────────────────
// §3.4 — Capacidad terma residencial
// ──────────────────────────────────────────────────────────────────

export const TERMA = {
  /** Tanque de almacenamiento: 1/5 de la dotación AC diaria. */
  TANQUE_FRAC: 1 / 5,
  /** Capacidad horaria del equipo: 1/7 de la dotación AC diaria. */
  HORARIA_FRAC: 1 / 7,
} as const;
